/* eslint-disable @animech/axiom/no-references-in-top-level-values */
import { Material } from "@animech/axiom-format/src/definitions/definition-bindings";
import {
	ChairSlot,
	ChairType,
	MaterialOptions,
	MaterialTheme,
	RoundTableType,
	SIDE_COUNT,
	SeatingType,
	Side,
	TableType,
	Vec3,
	isRoundTable,
	seatingTypeUniverse,
} from "./type-definitions";

/* eslint-disable @animech/axiom/strict-function-syntax */

// ---------------------------------------------------------------------------
// Seating dimensions (width in meters)
// ---------------------------------------------------------------------------
export const chairWidth: Record<SeatingType, number> = {
	"Chair-A2": 0.603,
	"Chair-HighTech": 0.398,
	"Chair-Bovik": 0.605,
	"Chair-Libelle": 0.641,
	"Chair-1": 0.419,
	"Bench-8": 1.1,
	"Bench-9": 1.7,
	"Bench-Libelle": 1.2,
	"Sofa-3": 1.382,
	"Sofa-5": 1.982,
};

// ---------------------------------------------------------------------------
// Table lengths (for long-side position spacing)
// ---------------------------------------------------------------------------
export const tableLength: Record<TableType, number> = {
	"Table-B31-84": 0.84,
	"Table-B31-170": 1.7,
	"Table-B31-230": 2.3,
	"Table-6B-60": 0.6,
	"Table-9A-100": 1.0,
	"Table-9A-120": 1.2,
	"Table-B25A-120": 1.2,
	"Table-HighTech-60": 0.6,
	"Table-HighTech-110": 1.1,
	"Table-Libelle-140": 1.4,
	"Table-Libelle-70": 0.7,
	"Table-Bovik-150": 1.5,
};

// ---------------------------------------------------------------------------
// Default chair per table (used when table changes to max-populate)
// ---------------------------------------------------------------------------
export const defaultChair: Record<TableType, ChairType> = {
	"Table-B31-84": "Chair-A2",
	"Table-B31-170": "Chair-A2",
	"Table-B31-230": "Chair-A2",
	"Table-6B-60": "Chair-1",
	"Table-9A-100": "Chair-A2",
	"Table-9A-120": "Chair-A2",
	"Table-B25A-120": "Chair-1",
	"Table-HighTech-60": "Chair-HighTech",
	"Table-HighTech-110": "Chair-HighTech",
	"Table-Libelle-140": "Chair-Libelle",
	"Table-Libelle-70": "Chair-Libelle",
	"Table-Bovik-150": "Chair-Bovik",
};

// ---------------------------------------------------------------------------
// Circular table radius
// ---------------------------------------------------------------------------
export const circularRadius: Record<RoundTableType, number> = {
	"Table-6B-60": 0.6,
	"Table-9A-100": 0.8,
	"Table-9A-120": 0.9,
	"Table-HighTech-60": 0.6,
};

// ---------------------------------------------------------------------------
// Max round chairs per table/seating combo
// ---------------------------------------------------------------------------
export const maxRoundChairs: Record<
	RoundTableType,
	Record<SeatingType, number>
> = {
	"Table-6B-60": {
		"Chair-A2": 2,
		"Chair-HighTech": 3,
		"Chair-Bovik": 2,
		"Chair-1": 3,
		"Chair-Libelle": 3,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-9A-100": {
		"Chair-A2": 5,
		"Chair-HighTech": 5,
		"Chair-Bovik": 5,
		"Chair-1": 5,
		"Chair-Libelle": 5,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-9A-120": {
		"Chair-A2": 6,
		"Chair-HighTech": 6,
		"Chair-Bovik": 6,
		"Chair-1": 6,
		"Chair-Libelle": 6,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-HighTech-60": {
		"Chair-A2": 2,
		"Chair-HighTech": 3,
		"Chair-Bovik": 2,
		"Chair-1": 3,
		"Chair-Libelle": 3,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
};

// ---------------------------------------------------------------------------
// Long-side max seating per table/seating combo (rectangular layout)
// ---------------------------------------------------------------------------
export const maxLongSide: Record<TableType, Record<SeatingType, number>> = {
	"Table-B31-84": {
		"Chair-A2": 1,
		"Chair-HighTech": 1,
		"Chair-Bovik": 1,
		"Chair-1": 1,
		"Chair-Libelle": 1,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-B31-170": {
		"Chair-A2": 2,
		"Chair-HighTech": 3,
		"Chair-Bovik": 2,
		"Chair-1": 3,
		"Chair-Libelle": 2,
		"Bench-8": 1,
		"Bench-9": 1,
		"Bench-Libelle": 0,
		"Sofa-3": 1,
		"Sofa-5": 1,
	},
	"Table-B31-230": {
		"Chair-A2": 3,
		"Chair-HighTech": 4,
		"Chair-Bovik": 3,
		"Chair-1": 4,
		"Chair-Libelle": 3,
		"Bench-8": 2,
		"Bench-9": 1,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 1,
	},
	// Round table — maxLongSide values unused; present only to satisfy Record<TableType, ...>.
	// Chair layout uses maxRoundChairs instead. See isRoundTable() and getMaxPopulation().
	"Table-6B-60": {
		"Chair-A2": 2,
		"Chair-HighTech": 2,
		"Chair-Bovik": 2,
		"Chair-1": 2,
		"Chair-Libelle": 2,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-9A-100": {
		"Chair-A2": 2,
		"Chair-HighTech": 2,
		"Chair-Bovik": 2,
		"Chair-1": 2,
		"Chair-Libelle": 2,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-9A-120": {
		"Chair-A2": 4,
		"Chair-HighTech": 4,
		"Chair-Bovik": 4,
		"Chair-1": 4,
		"Chair-Libelle": 4,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-B25A-120": {
		"Chair-A2": 2,
		"Chair-HighTech": 2,
		"Chair-Bovik": 2,
		"Chair-1": 2,
		"Chair-Libelle": 2,
		"Bench-8": 1,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 1,
		"Sofa-5": 0,
	},
	"Table-HighTech-60": {
		"Chair-A2": 2,
		"Chair-HighTech": 2,
		"Chair-Bovik": 2,
		"Chair-1": 2,
		"Chair-Libelle": 2,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-HighTech-110": {
		"Chair-A2": 1,
		"Chair-HighTech": 2,
		"Chair-Bovik": 1,
		"Chair-1": 2,
		"Chair-Libelle": 1,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-Libelle-140": {
		"Chair-A2": 2,
		"Chair-HighTech": 2,
		"Chair-Bovik": 2,
		"Chair-1": 2,
		"Chair-Libelle": 2,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 1,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-Libelle-70": {
		"Chair-A2": 1,
		"Chair-HighTech": 1,
		"Chair-Bovik": 1,
		"Chair-1": 1,
		"Chair-Libelle": 1,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
	"Table-Bovik-150": {
		"Chair-A2": 2,
		"Chair-HighTech": 3,
		"Chair-Bovik": 2,
		"Chair-1": 3,
		"Chair-Libelle": 2,
		"Bench-8": 0,
		"Bench-9": 0,
		"Bench-Libelle": 0,
		"Sofa-3": 0,
		"Sofa-5": 0,
	},
};

// ---------------------------------------------------------------------------
// Short-side (left/right) chair offset from table center along X axis
// ---------------------------------------------------------------------------
export const shortSideOffset: Record<TableType, number> = {
	"Table-B31-84": 0.62,
	"Table-B31-170": 1.05,
	"Table-B31-230": 1.35,
	"Table-6B-60": 0.5,
	"Table-9A-100": 0.7,
	"Table-9A-120": 0.8,
	"Table-B25A-120": 0.8,
	"Table-HighTech-60": 0.5,
	"Table-HighTech-110": 0.75,
	"Table-Libelle-140": 0.9,
	"Table-Libelle-70": 0.55,
	"Table-Bovik-150": 0.95,
};

// ---------------------------------------------------------------------------
// Long-side Z offset (distance from table center to seating row, per type)
// Sofas and benches are pushed further out to avoid crowding the table.
// ---------------------------------------------------------------------------
export const longSideZ: Record<SeatingType, number> = {
	"Chair-A2": 0.6,
	"Chair-HighTech": 0.6,
	"Chair-Bovik": 0.6,
	"Chair-Libelle": 0.6,
	"Chair-1": 0.6,
	"Bench-8": 0.75,
	"Bench-9": 0.75,
	"Bench-Libelle": 0.85,
	"Sofa-3": 0.75,
	"Sofa-5": 0.75,
};

// ---------------------------------------------------------------------------
// Derive MAX_CHAIRS from data tables
// ---------------------------------------------------------------------------
const maxRect = Math.max(
	...Object.values(maxLongSide).flatMap((chairs) => Object.values(chairs)),
);
const maxRound = Math.max(
	...Object.values(maxRoundChairs).flatMap((chairs) => Object.values(chairs)),
);
// rect: top + bottom + left + right = 2 * maxLongSide + 2
export const MAX_CHAIRS = Math.max(maxRect * 2 + 2, maxRound);

// Total slots = 5 representative slots (one per side) + MAX_CHAIRS worker/padding slots
export const TOTAL_CHAIR_SLOTS: number = SIDE_COUNT + MAX_CHAIRS;

// ---------------------------------------------------------------------------
// Allowed seating types for long sides of a given table
// ---------------------------------------------------------------------------
export function getAllowedSeatingForLongSide(table: TableType): SeatingType[] {
	const tableData = maxLongSide[table];
	return seatingTypeUniverse.filter((s) => tableData[s] > 0);
}

// ---------------------------------------------------------------------------
// Max population for a given table (used on table change)
// ---------------------------------------------------------------------------
export type MaxPopulation = {
	chair: ChairType;
	topChair: ChairType;
	bottomChair: ChairType;
	leftChair: ChairType;
	rightChair: ChairType;
	topCount: number;
	bottomCount: number;
	leftCount: number;
	rightCount: number;
	chairCount: number;
};

export function getMaxPopulation(table: TableType): MaxPopulation {
	const chair = defaultChair[table];
	if (isRoundTable(table)) {
		const count = maxRoundChairs[table]?.[chair] ?? 0;
		return {
			chair,
			topChair: chair,
			bottomChair: chair,
			leftChair: chair,
			rightChair: chair,
			topCount: 0,
			bottomCount: 0,
			leftCount: 0,
			rightCount: 0,
			chairCount: count,
		};
	}
	const longSide = maxLongSide[table]?.[chair] ?? 0;
	return {
		chair,
		topChair: chair,
		bottomChair: chair,
		leftChair: chair,
		rightChair: chair,
		topCount: longSide,
		bottomCount: longSide,
		leftCount: 1,
		rightCount: 1,
		chairCount: 0,
	};
}

// ---------------------------------------------------------------------------
// ChairSlot helpers
// ---------------------------------------------------------------------------
export type { ChairSlot } from "./type-definitions";

export const hiddenSlot: ChairSlot = {
	visible: false,
	pos: { x: 0, y: 0, z: 0 },
	rot: { x: 0, y: 0, z: 0 },
	chairType: "Chair-A2",
	chairMaterialWood: undefined,
	chairMaterialMetal: "WoodPineOiled",
	chairMaterialWeld: "WoodPineOiled",
	// Endcap is derived from chairMaterialWood by the chairMaterialWoodDerived
	// template rule. wood=undefined → endcap=undefined; aligned here so the
	// initial $state matches what the rule writes after the first cascade.
	chairMaterialEndcap: undefined,
	chairMaterialScrewAtlas: "AtlasScrews",
	chairMaterialScrewPhillips: "ScrewRoundedPhillips-M3x10mm-A",
	sideId: "top",
	count: 0,
	chairTheme: "theme-teak-galvanized",
};

export function makeChairSlot(
	visible: boolean,
	pos: Vec3,
	rot: Vec3,
	chairType: SeatingType,
	chairMaterialWood: Material | undefined,
	chairMaterialMetal: Material,
	chairMaterialWeld: Material,
	chairMaterialEndcap: Material | undefined,
	chairMaterialScrewAtlas: Material,
	chairMaterialScrewPhillips: Material,
	sideId: Side = "top",
	count: number = 0,
	chairTheme: MaterialTheme = "theme-teak-galvanized",
): ChairSlot {
	return {
		visible,
		pos,
		rot,
		chairType,
		chairMaterialWood,
		chairMaterialMetal,
		chairMaterialWeld,
		chairMaterialEndcap,
		chairMaterialScrewAtlas,
		chairMaterialScrewPhillips,
		sideId,
		count,
		chairTheme,
	};
}

export function padSlots(slots: ChairSlot[]): ChairSlot[] {
	const padding = new Array(MAX_CHAIRS - slots.length).fill(
		hiddenSlot,
	) as ChairSlot[];
	return [...slots, ...padding];
}

export const chairOptions: Record<SeatingType, MaterialOptions> = {
	"Chair-A2": {
		wood: [
			"WoodTeakUntreated",
			"WoodOakOiled",
			"WoodPineOiled",
			"LacquerWhite",
			"PaintWoodGreen",
			"PaintWoodRed",
		],
		metal: ["MetalGalvanizedSteel", "PaintMetalBlack", "PaintMetalGreen"],
		excludedCombos: [
			{ wood: "WoodPineOiled", metal: "PaintMetalBlack" },
			{ wood: "WoodPineOiled", metal: "MetalGalvanizedSteel" },
			{ wood: "WoodTeakUntreated", metal: "PaintMetalGreen" },
			{ wood: "WoodOakOiled", metal: "PaintMetalGreen" },
			{ wood: "LacquerWhite", metal: "PaintMetalGreen" },
			{ wood: "PaintWoodGreen", metal: "PaintMetalGreen" },
			{ wood: "PaintWoodRed", metal: "PaintMetalGreen" },
		],
	},
	"Chair-1": {
		wood: [
			"WoodTeakUntreated",
			"WoodOakOiled",
			"WoodPineOiled",
			"LacquerWhite",
			"PaintWoodGreen",
			"PaintWoodRed",
		],
		metal: ["MetalGalvanizedSteel", "PaintMetalBlack", "PaintMetalGreen"],
		excludedCombos: [
			{ wood: "WoodPineOiled", metal: "PaintMetalBlack" },
			{ wood: "WoodPineOiled", metal: "MetalGalvanizedSteel" },
			{ wood: "WoodTeakUntreated", metal: "PaintMetalGreen" },
			{ wood: "WoodOakOiled", metal: "PaintMetalGreen" },
			{ wood: "LacquerWhite", metal: "PaintMetalGreen" },
			{ wood: "PaintWoodGreen", metal: "PaintMetalGreen" },
			{ wood: "PaintWoodRed", metal: "PaintMetalGreen" },
		],
	},
	"Chair-HighTech": {
		wood: [],
		metal: [
			"MetalGalvanizedSteel",
			"PaintMetalPowdercoatBlack",
			"PaintMetalPowdercoatChestnut",
			"PaintMetalPowdercoatIvory",
			"PaintMetalPowdercoatPetrol",
		],
	},
	"Chair-Bovik": {
		wood: ["WoodTeakUntreated"],
		metal: ["MetalGalvanizedSteel", "PaintMetalBlack"],
	},
	"Chair-Libelle": {
		wood: [],
		metal: ["PaintMetalGreen", "PaintMetalGrey", "PaintMetalGraphite"],
	},
	"Bench-8": {
		wood: ["WoodTeakUntreated", "WoodOakOiled", "LacquerWhite"],
		metal: ["MetalGalvanizedSteel"],
	},
	"Bench-9": {
		wood: ["WoodTeakUntreated", "WoodOakOiled", "LacquerWhite"],
		metal: ["MetalGalvanizedSteel"],
	},
	"Bench-Libelle": {
		wood: [],
		metal: ["PaintMetalGreen", "PaintMetalGrey", "PaintMetalGraphite"],
	},
	"Sofa-3": {
		wood: ["WoodTeakUntreated", "WoodOakOiled", "LacquerWhite"],
		metal: ["MetalGalvanizedSteel", "PaintMetalBlack"],
		excludedCombos: [{ wood: "LacquerWhite", metal: "PaintMetalBlack" }],
	},
	"Sofa-5": {
		wood: ["WoodTeakUntreated", "WoodOakOiled", "LacquerWhite"],
		metal: ["MetalGalvanizedSteel", "PaintMetalBlack"],
		excludedCombos: [{ wood: "LacquerWhite", metal: "PaintMetalBlack" }],
	},
};
